import { useEffect, useState } from "react";
import {
    FiEye,
    FiEyeOff,
    FiLock,
    FiX,
    FiUser,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { post_UserLogin } from "../services/Login";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import useAuth from "../hooks/useAuth";
import useAccessControl from "../hooks/useAccessControl";
import { getAccessControl } from "../services/AccessControl";
import { getAllowDenyStatus } from "../services/AllowStatus";
import { baseURL } from "../config/api";
import { appAdminRoutes } from "../pages/routes/adminRoutes";
import { appAdminSettingRoutes } from "../pages/routes/adminSettingRoutes";

interface LoginModalProps {
    open: boolean;
    onClose: () => void;
}

const Login = ({ open, onClose }: LoginModalProps) => {
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const navigate = useNavigate();
    const { setAuth }: any = useAuth();
    const { setUserRoleId, setPermissions }: any = useAccessControl();
    const [rememberMe, setRememberMe] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [fieldReadOnly, setFieldReadOnly] = useState(false);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalPadding = document.body.style.paddingRight;

        if (open) {
            const scrollbarWidth =
                window.innerWidth - document.documentElement.clientWidth;

            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPadding;
        };
    }, [open]);

    if (!open) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (isProcessing) return;

        setIsProcessing(true);
        setFieldReadOnly(true);
        setErrorMessage("");

        post_UserLogin({
            login: username,
            password,
        })
            .then((response: any) => {
                handleLoginSuccess(response.access_token);
            })
            .catch((error: any) => {
                setIsProcessing(false);
                setFieldReadOnly(false);
                setErrorMessage(
                    error?.response?.data?.message ||
                    "Invalid username or password."
                );
            });
    };

    const handleLoginSuccess = (accessToken: string) => {
        const decoded: any = jwtDecode(accessToken);

        const id = decoded.sub;
        const emailAddress = decoded.email;
        const givenname = decoded.username;
        const roles = decoded.role ?? "";
        const expiryDate = new Date(decoded.exp * 1000);
        const expiration =
            rememberMe
                ? expiryDate
                : Math.floor(Date.now() / 1000) + 3600;

        Cookies.set("token-velox-charter", accessToken, {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });

        Cookies.set("role-velox-charter", roles, {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });        

        Cookies.set("userId-velox-charter", id, {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });

        Cookies.set("userName-velox-charter", username, {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });

        Cookies.set("fullName-velox-charter", givenname, {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });

        Cookies.set("expires-velox-charter", expiration.toString(), {
            expires: rememberMe
                ? expiryDate
                : new Date(Date.now() + 60 * 60 * 1000),
        });

        setAuth({
            id,
            userName: username,
            givenname,
            emailAddress,
            roles,
            accessToken,
        });

        setUsername("");
        setPassword("");

        getUserProfile(
            id,
            rememberMe ? decoded.exp : expiration,
            roles,
            accessToken
        );

        onClose();
    };

    const fetchPermissions = async (role: string, roleId: number, token: string) => {
        if (role.toLowerCase() === "admin") {
            navigate("/admin/dashboard", { replace: true });
            return;
        }

        try {
            const res: any = await getAccessControl("access_controls/", { roleId }, token);
            const accessControlData = res.data;

            const response: any = await getAllowDenyStatus();

            const allowStatusId = response.data.find(
                (item: any) => item.name === "Allow All"
            )?.id;

            const newPermissions: Record<string, boolean> = {};

            accessControlData.forEach((item: any) => {
                const normalized = item.section
                    .replace(/\s+/g, "")
                    .replace(/-/g, "")
                    .toLowerCase();

                newPermissions[normalized] =
                    item.allowDenyStatusId === allowStatusId;
            });

            setPermissions(newPermissions);

            handleNavigationForAppRoutes(newPermissions);

        } catch (error) {
            setIsProcessing(false);
            setFieldReadOnly(false);
            console.error("Error fetching permissions:", error);
        }
    };

    const getUserProfile = (
        id: string,
        expires: any,
        roles: any,
        token: string
    ) => {

        fetch(`${baseURL}users/${parseInt(id)}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to fetch user profile");
                }
                return res.json();
            })
            .then((userData) => {


                const roleId = userData?.data?.roleId;

                const profileImg = userData?.data?.profilePic;

                setUserRoleId(roleId);

                Cookies.set("roleId-velox-charter", roleId, {
                    expires,
                });

                Cookies.set("profileImg-velox-charter", profileImg, {
                    expires,
                });

                fetchPermissions(roles, roleId, token);

                setIsProcessing(false);
                setFieldReadOnly(false);
            })
            .catch((error) => {
                setIsProcessing(false);
                setFieldReadOnly(false);
                console.error("Error fetching user profile:", error);
            });
    };


    const handleNavigationForAppRoutes = (permissions: any) => {

        const dashboardRoute = {
            path: "/admin/dashboard",
            section: "dashboard"
        };

        const hasDashboardPermission = permissions?.[dashboardRoute.section] || false;

        if (hasDashboardPermission) {
            navigate("/admin/dashboard", { replace: true });
            return;
        }

        const nextAvailableRoute = appAdminRoutes.find((route: any) => {
            return permissions?.[route.section] || false;
        });

        if (nextAvailableRoute) {
            navigate(nextAvailableRoute.path, { replace: true });
            return;
        }

        const nextAdminRoute = appAdminSettingRoutes
            .flatMap((route: any) => route.child || [])
            .find((route: any) => permissions?.[route.section]);

        if (nextAdminRoute) {
            navigate(nextAdminRoute.path, { replace: true });
            return;
        }

        navigate("/unauthorized", { replace: true });
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-5">

            <div className="relative w-full max-w-6xl overflow-hidden rounded-[30px] bg-white shadow-[0_30px_90px_rgba(0,0,0,0.35)] animate-[fadeIn_.25s_ease]">

                <button
                    onClick={onClose}
                    className="absolute right-6 top-6 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 transition hover:bg-red-500 hover:text-white"
                >
                    <FiX size={22} />
                </button>

                <div className="grid lg:grid-cols-2">

                    <div className="relative hidden lg:block">

                        <img
                            src="/images/aboutus1.png"
                            alt=""
                            className="h-full w-full object-cover"
                        />

                        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/40 to-black/60" />

                        <div className="absolute inset-0 flex flex-col justify-center p-16 text-white">

                            <span className="mb-4 inline-flex w-fit rounded-full bg-white/20 px-5 py-2 text-sm backdrop-blur">
                                Premium Charter Service
                            </span>

                            <h2 className="text-5xl font-bold leading-tight">
                                Travel
                                <br />
                                Without
                                <br />
                                Limits.
                            </h2>

                            <p className="mt-8 max-w-md text-lg leading-8 text-white/90">
                                Experience luxury charter transportation with
                                safety, comfort and reliability for every trip.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center justify-center p-10 lg:p-16">

                        <div className="w-full max-w-md">

                            <span className="rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                                Welcome Back
                            </span>

                            <h1 className="mt-5 text-4xl font-bold text-gray-900">
                                Sign In
                            </h1>

                            <p className="mt-3 text-gray-500">
                                Sign in to access your Velox Charter dashboard.
                            </p>

                            <form
                                onSubmit={handleSubmit}
                                className="mt-10 space-y-7"
                            >
                                <div>

                                    <label className="mb-2 block font-medium text-gray-700">
                                        Username
                                    </label>

                                    <div className="flex items-center border-b-2 border-gray-300 pb-3 transition focus-within:border-primary">

                                        <FiUser
                                            className="text-gray-400"
                                            size={20}
                                        />

                                        <input
                                            type="text"
                                            placeholder="Enter your username"
                                            value={username}
                                            onChange={(e) =>
                                                setUsername(e.target.value)
                                            }
                                            readOnly={fieldReadOnly}
                                            className="ml-4 w-full bg-transparent outline-none placeholder:text-gray-400"
                                        />

                                    </div>
                                </div>

                                <div>

                                    <label className="mb-2 block font-medium text-gray-700">
                                        Password
                                    </label>

                                    <div className="flex items-center border-b-2 border-gray-300 pb-3 transition focus-within:border-primary">

                                        <FiLock
                                            className="text-gray-400"
                                            size={20}
                                        />

                                        <input
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            placeholder="Enter password"
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            readOnly={fieldReadOnly}
                                            className="ml-4 w-full bg-transparent outline-none placeholder:text-gray-400"
                                        />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(
                                                    !showPassword
                                                )
                                            }
                                        >
                                            {showPassword ? (
                                                <FiEyeOff
                                                    className="text-gray-400"
                                                    size={20}
                                                />
                                            ) : (
                                                <FiEye
                                                    className="text-gray-400"
                                                    size={20}
                                                />
                                            )}
                                        </button>

                                    </div>

                                </div>

                                <div className="flex items-center justify-between text-sm">

                                    <label className="flex items-center gap-2">

                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={() => setRememberMe(!rememberMe)}
                                            className="accent-primary"
                                        />

                                        Remember Me

                                    </label>

                                    <button
                                        type="button"
                                        className="font-medium text-primary hover:underline"
                                    >
                                        Forgot Password?
                                    </button>

                                </div>

                                <button
                                    type="submit"
                                    disabled={isProcessing}
                                    className="mt-5 w-full rounded-2xl bg-primary py-4 text-lg font-semibold text-white transition duration-300 hover:-translate-y-1 hover:shadow-xl disabled:opacity-70"
                                >
                                    {isProcessing ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                            Signing In...
                                        </div>
                                    ) : (
                                        "Sign In"
                                    )}
                                </button>
                                {errorMessage && (
                                    <p className="mt-4 text-center text-sm text-red-500">
                                        {errorMessage}
                                    </p>
                                )}
                            </form>

                        </div>
                    </div>
                </div>
            </div>

            <style>
                {`
                    @keyframes fadeIn{
                        from{
                            opacity:0;
                            transform:scale(.95);
                        }
                        to{
                            opacity:1;
                            transform:scale(1);
                        }
                    }
                `}
            </style>

        </div>
    );
};

export default Login;